import type { JSONContent } from "@tiptap/core";
import type {
  AdminUserFilterQuery,
  AdminNotificationUserGroup,
  PageDto,
} from "./client";

export type CampaignFilters = Omit<AdminUserFilterQuery, "q"> & {
  query?: string;
  registeredFrom?: string;
  registeredTo?: string;
  lastSeenBefore?: string;
  mobilityRole?: "DRIVER" | "RIDER" | "BOTH";
  emailOptedOut?: "any" | "true" | "false";
};
export type CampaignPayload = {
  channel: "EMAIL" | "PUSH";
  campaignName: string;
  subject: string;
  title: string;
  body: string;
  content: JSONContent | null;
  userGroup: AdminNotificationUserGroup | "LEADS" | "SURVEY_LEADS";
  surveyId?: string | null;
  communityId: string | null;
  eventId: string | null;
  userIds: string[];
  screen: string | null;
  actionLabel: string;
  actionUrl: string;
  filters: CampaignFilters;
};
export type CampaignMetrics = Record<
  | "targeted"
  | "excluded"
  | "queued"
  | "accepted"
  | "delivered"
  | "opened"
  | "clicked"
  | "unsubscribed"
  | "failed"
  | "bounced"
  | "complained"
  | "botOpened"
  | "unknownOpened"
  | "botClicked"
  | "unknownClicked",
  number
> & {
  openRate: number | null;
  clickRate: number | null;
  unsubscribeRate: number | null;
};
export type Campaign = CampaignPayload & {
  surveyTitle?: string | null;
  id: string;
  draft: boolean;
  createdAt: string;
  sentAt: string | null;
  metrics: CampaignMetrics;
};
export type CampaignRecipient = {
  userId: string | null;
  leadId?: string | null;
  recipientId?: string;
  recipientType?: "USER" | "LEAD";
  name: string;
  email: string;
  status: string;
  reason: string | null;
  acceptedAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  unsubscribedAt: string | null;
  bouncedAt: string | null;
  complainedAt: string | null;
  botOpenedAt: string | null;
  unknownOpenedAt: string | null;
  botClickedAt: string | null;
  unknownClickedAt: string | null;
  communication: {
    deliveries: Array<{
      attemptedAt: string;
      status: string;
      errorCode: string | null;
      errorMessage: string | null;
    }>;
    events: Array<{
      type: string;
      occurredAt: string;
      human: boolean | null;
      reason: string | null;
    }>;
  } | null;
};
export type Audience = {
  targeted: number;
  eligible: number;
  excluded: number;
  recipients: PageDto<CampaignRecipient>;
};
type Request = (input: RequestInfo, init?: RequestInit) => Promise<any>;
const base = "/admin/notifications/campaigns";
function wire(payload: CampaignPayload) {
  const filters = Object.fromEntries(
    Object.entries(payload.filters || {})
      .filter(([, v]) => v !== "" && v !== "any" && v != null)
      .map(([k, v]) => [k, v === "true" ? true : v === "false" ? false : v])
  );
  return { ...payload, filters };
}
export const campaignApi = (request: Request) => ({
  surveys: (communityId: string, page = 0): Promise<PageDto<{ id: string; title: string; code: string | null; status: string }>> =>
    request(`${base}/surveys?communityId=${encodeURIComponent(communityId)}&page=${page}&size=100`),
  list: (page = 0): Promise<PageDto<Campaign>> =>
    request(`${base}?page=${page}&size=10`),
  get: (id: string): Promise<Campaign> =>
    request(`${base}/${encodeURIComponent(id)}`),
  save: (payload: CampaignPayload, id?: string): Promise<Campaign> =>
    request(`${base}${id ? "/" + encodeURIComponent(id) : ""}`, {
      method: id ? "PUT" : "POST",
      body: JSON.stringify(wire(payload)),
    }),
  send: (id: string): Promise<Campaign> =>
    request(`${base}/${encodeURIComponent(id)}/send`, { method: "POST" }),
  test: (id: string, userId: string): Promise<{ id: string; status: string }> =>
    request(`${base}/${encodeURIComponent(id)}/test`, {
      method: "POST",
      body: JSON.stringify({ userId }),
    }),
  preview: (payload: CampaignPayload): Promise<{ html: string }> =>
    request(`${base}/preview`, {
      method: "POST",
      body: JSON.stringify(wire(payload)),
    }),
  audience: (payload: CampaignPayload, page = 0): Promise<Audience> =>
    request(`${base}/audience?page=${page}&size=10`, {
      method: "POST",
      body: JSON.stringify(wire(payload)),
    }),
  recipients: (
    id: string,
    page = 0,
    status = ""
  ): Promise<PageDto<CampaignRecipient>> =>
    request(
      `${base}/${encodeURIComponent(
        id
      )}/recipients?page=${page}&size=10&status=${encodeURIComponent(status)}`
    ),
});
